import { useState, useCallback } from "react";
import type {
  BatchJobSummaryViewModel,
  ExternalRefCapabilityUsageViewModel,
} from "@models/monitoring";
import type { IProcessDetailViewModel } from "@models/extraction";
import { costApi } from "@services/costApi";
import { intelligenceEngineApi } from "@services/extractionApi";
import { mappingApi } from "@services/mappingApi";

/**
 * Loaders of the extraction-process and mapping-job detail pages: the entity
 * itself from its owning service (items, downloads, rows) and its cost per
 * capability from core. Lists, charts and transactions moved to core's
 * monitoring BFF (`@services/adminMonitoringApi`).
 */
export const useMonitoring = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async <T,>(label: string, load: () => Promise<T>): Promise<T | null> => {
    try {
      setIsLoading(true);
      setError(null);
      return await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to fetch ${label}`);
      console.error(`Error fetching ${label}:`, err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchMappingJobDetail = useCallback(
    (jobId: string): Promise<BatchJobSummaryViewModel | null> =>
      run("mapping job detail", async () => {
        const jobs = await mappingApi.getJobsBatch([jobId]);
        return jobs.find((job) => job.jobId === jobId) ?? null;
      }),
    [run],
  );

  const fetchProcessDetail = useCallback(
    (processId: string): Promise<IProcessDetailViewModel | null> =>
      run("process detail", () => intelligenceEngineApi.getProcessDetailWithItems(processId)),
    [run],
  );

  /** The cost of one resource (`extraction_process` / `mapping_job` plus its id) per capability. */
  const fetchProcessCapabilityUsage = useCallback(
    (refType: string, refId: string): Promise<ExternalRefCapabilityUsageViewModel[] | null> =>
      run("capability usage", () => costApi.getResourceUsage(refType, refId)),
    [run],
  );

  return { isLoading, error, fetchMappingJobDetail, fetchProcessDetail, fetchProcessCapabilityUsage };
};
